import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed, DestroyRef, ElementRef,
  HostBinding, HostListener,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';

import { RouterLink } from '@angular/router';
import { CurrencyPipe, DOCUMENT } from '@angular/common';
import { ComponentHelperService, OverlayService } from '@core/services';
import { ExchangeRatesFacade } from '@core/facades/exchange-rates.facade';
import { I18NEXT_SERVICE, I18NextModule } from 'angular-i18next';
import { LanguageLocales } from '@locales';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { isNil } from 'lodash';
import { CdkScrollable } from '@angular/cdk/overlay';
import { Store } from '@ngrx/store';
import { mediaActions } from '@store/media';
import { AnalyticsEventsEnum } from '@core/models/analytics.model';

type Coin = {
  name: string;
  img: string;
  link: string;
  price?: number;
};

@Component({
  standalone: true,
  selector: 'cheelee-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    CurrencyPipe,
    I18NextModule,
    CdkScrollable,
  ],
  providers: [CdkScrollable],
})
export class HeaderComponent implements OnInit, AfterViewInit {
  @HostListener('window:resize')
  windowResizeListener(): void {
    this.resizeHandler();
  }

  @HostBinding('class')
  get classes(): Record<string, boolean> {
    return {
      'chl-header--fixed': this.isFixed(),
      'chl-header--scrolled': this.isScrolledOverHeight(),
    };
  }

  readonly isFixed = input(false, { transform: booleanAttribute });

  private readonly doc = inject(DOCUMENT);

  private readonly destroyRef = inject(DestroyRef);

  private readonly elRef = inject(ElementRef);

  private readonly overlayService = inject(OverlayService);

  private readonly sc = inject(CdkScrollable);

  private readonly exchangeRatesFacade = inject(ExchangeRatesFacade);

  private readonly i18nService = inject(I18NEXT_SERVICE);

  private readonly headerHeight = inject(ComponentHelperService).headerHeight;

  private readonly store = inject(Store);

  private readonly isScrolledOverHeight = signal(false);

  protected readonly currentLang = signal<LanguageLocales>(LanguageLocales.RU);

  protected readonly LanguageLocales = LanguageLocales;

  protected readonly coins = computed<Coin[]>(() => {
    const lee = this.exchangeRatesFacade.lee();
    const cheelee = this.exchangeRatesFacade.cheelee();

    return [
      {
        name: 'Lee Coin',
        img: 'assets/img/icons/lee_coin.png',
        link: 'https://coinmarketcap.com/currencies/love-earn-enjoy/',
        price: lee,
      },
      {
        name: 'Cheelee Coin',
        img: 'assets/img/icons/cheelee_coin.png',
        link: 'https://coinmarketcap.com/currencies/cheelee/',
        price: cheelee,
      },
    ];
  });

  ngOnInit(): void {
    this.i18nService.events.languageChanged
      .pipe(filter(langCode => !isNil(langCode)))
      .pipe(filter(langCode => Object.values(LanguageLocales).includes(langCode as LanguageLocales)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lang => this.currentLang.set(lang as LanguageLocales));

    this.scrollListener();
  }

  ngAfterViewInit(): void {
    this.resizeHandler();
  }


  protected switchLanguage(): void {
    const currentLang = this.i18nService.language;
    this.i18nService.changeLanguage(currentLang === LanguageLocales.RU ? LanguageLocales.EN : LanguageLocales.RU);
  }

  protected openGetAppDialog(): void {
    this.overlayService.openGetAppDialog(AnalyticsEventsEnum.FeedModalAppear);
  }

  private resizeHandler(): void {
    const nativeEl = this.elRef.nativeElement as HTMLElement;

    this.headerHeight.set(nativeEl.offsetHeight);
  }

  private scrollListener(): void {
    this.sc.elementScrolled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const elRef = this.sc.getElementRef();
        const offset = elRef.nativeElement.scrollTop;
        const isScrolledOverHeight = offset > this.headerHeight();
        this.isScrolledOverHeight.set(isScrolledOverHeight);
      });
  }

  reload() {
    this.store.dispatch(mediaActions.loadFeedMedia({ isNextPart: false }));
  }
}
